<template>
  <BittsLoading :is-loading="!isReady">
    <div class="api-webhooks-tab">
      <ApiTabEmptyState
        v-if="!webhookCards.length"
        title="No webhooks yet"
        description="Send Crossbeam signals to your own systems as they happen. Create a webhook to choose the events and the destination."
        create-cta-text="Create webhook"
        :icon="['fak', 'share']"
        @create="openCreate('webhooks')"
      />
      <template v-else>
        <ApiResourceCard
          v-for="webhook in webhookCards"
          :key="webhook.id"
          :name="webhook.name"
          :status="webhook.status"
          :status-text="webhook.statusText"
        >
          <template #actions>
            <ApiResourceCardActions
              action-text="Settings"
              :action-icon="['fak', 'settings']"
              :resource-name="webhook.name"
              :menu-options="webhook.menuOptions"
              @action="openDetail('webhooks', webhook.id)"
              @menu-select="(value) => handleMenuSelect(webhook.id, value)"
            />
          </template>
        </ApiResourceCard>
      </template>
      <BittsModal
        v-if="webhookPendingRemove"
        variant="confirm"
        title="Remove webhook"
        :description="removeDescription"
        primary-button-text="Remove webhook"
        primary-button-type="danger"
        secondary-button-text="Cancel"
        :action-loading="isRemoving"
        @action="handleRemove"
        @closed="webhookPendingRemove = null"
      />
    </div>
  </BittsLoading>
</template>

<script setup lang="ts">
import { BittsLoading, BittsModal } from '@crossbeam/bitts';

import { useHead } from '@unhead/vue';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import {
  useFlashesStore,
  useIntegrationProfileStore,
  useWebhooksStore,
} from '@/stores';
import type { Webhook, WebhookStatus } from '@/types/webhooks';
import { WEBHOOK_STATUS_CONFIG } from '@/types/webhooks';

import ApiResourceCard from '../components/ApiResourceCard.vue';
import ApiResourceCardActions from '../components/ApiResourceCardActions.vue';
import ApiTabEmptyState from '../components/ApiTabEmptyState.vue';
import type { ApiCardMenuOptionType } from '../types';
import useApiRoutes from '../useApiRoutes';

useHead({ title: 'Webhooks - API - Crossbeam' });

const { openCreate, openDetail } = useApiRoutes();
const flashesStore = useFlashesStore();
const webhooksStore = useWebhooksStore();
const integrationProfileStore = useIntegrationProfileStore();
const { ready: isReady } = storeToRefs(webhooksStore);

const ROTATE_SECRET_OPTION: ApiCardMenuOptionType = {
  label: 'Rotate signing secret',
  value: 'rotate-secret',
  faIcon: ['fas', 'arrows-rotate'],
};

const REMOVE_OPTION: ApiCardMenuOptionType = {
  label: 'Remove webhook',
  value: 'remove',
  faIcon: ['fas', 'trash-can'],
};

const webhookCards = computed(() =>
  webhooksStore.webhooks.map((webhook) => ({
    id: webhook.id,
    name: webhook.name,
    status: WEBHOOK_STATUS_CONFIG[webhook.status].status,
    statusText: WEBHOOK_STATUS_CONFIG[webhook.status].text,
    menuOptions: [
      ...(webhook.status === 'untested' ? [] : [toggleOption(webhook.status)]),
      ROTATE_SECRET_OPTION,
      REMOVE_OPTION,
    ],
  })),
);

function toggleOption(status: WebhookStatus): ApiCardMenuOptionType {
  const isActive = status === 'active';
  return {
    label: isActive ? 'Disable webhook' : 'Enable webhook',
    value: isActive ? 'disable' : 'enable',
    faIcon: ['fas', isActive ? 'circle-pause' : 'circle-play'],
  };
}

const webhookPendingRemove = ref<Webhook | null>(null);
const isRemoving = ref(false);

const removeDescription = computed(
  () =>
    `"${webhookPendingRemove.value?.name}" will stop receiving signals. This action cannot be undone.`,
);

async function setStatus(webhookId: string, status: 'active' | 'paused') {
  const updated = await webhooksStore.updateWebhook(webhookId, { status });
  if (!updated) return;
  flashesStore.addSuccessFlash({
    message: status === 'active' ? 'Webhook enabled' : 'Webhook disabled',
  });
}

function handleMenuSelect(webhookId: string, value: string) {
  if (value === 'enable') return setStatus(webhookId, 'active');
  if (value === 'disable') return setStatus(webhookId, 'paused');
  if (value === 'rotate-secret') {
    return openDetail('webhooks', webhookId, {
      query: { action: 'rotate-secret' },
    });
  }
  webhookPendingRemove.value = webhooksStore.getWebhookById(webhookId) ?? null;
}

async function handleRemove() {
  const webhook = webhookPendingRemove.value;
  if (!webhook) return;
  isRemoving.value = true;
  try {
    const hasRemoved = await webhooksStore.deleteWebhook(webhook.id);
    if (!hasRemoved) return;
    webhook.filters.forEach(({ profile_id }) =>
      integrationProfileStore.deleteIntegrationProfileInStore(profile_id),
    );
    flashesStore.addSuccessFlash({ message: 'Webhook deleted successfully' });
    webhookPendingRemove.value = null;
  } finally {
    isRemoving.value = false;
  }
}
</script>
